import React, { Suspense, useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { ActionIcon, Box, Loader as MantineLoader, Text, Alert, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconArrowsMaximize, IconArrowsMinimize } from '@tabler/icons-react';
import { BRAND_PRIMARY } from '../../theme/brandTheme.js';
import { getGLBUrl, clearGLBFailure } from '../../utils/glbProxy';
import Localization from '../../Localization.jsx';
import {
  Box3 as ThreeBox3,
  Vector3 as ThreeVector3,
  BufferAttribute as ThreeBufferAttribute,
  DoubleSide as ThreeDoubleSide,
} from 'three';

// webpack/ESM interop: `require('three')` may put constructors on `.default`
// (or `.default.default`). `typeof === 'function'` is required FOR CONSTRUCTORS
// AND HOOKS — a truthy namespace object is not a constructor, and `new Box3()`
// then throws, which in CRA pops a full-screen overlay and blocks the rest of
// the editor.
//
// It must NOT be used for the react components: Canvas, OrbitControls, Center
// and Html are React.forwardRef(...) values, i.e. OBJECTS ({$$typeof: Symbol
// (react.forward_ref), render}), so a function test rejects all four, leaves
// hasThreeJsDeps false and silently degrades every 3D preview in the editor to
// the no-WebGL fallback. Components go through pickValue below.
function pickFn(mod, name) {
  if (!mod) return undefined;
  if (typeof mod[name] === 'function') return mod[name];
  const d = mod.default;
  if (d && typeof d[name] === 'function') return d[name];
  if (d && d.default && typeof d.default[name] === 'function') return d.default[name];
  return undefined;
}

function pickValue(mod, name) {
  if (!mod) return undefined;
  if (mod[name] !== undefined) return mod[name];
  const d = mod.default;
  if (d && d[name] !== undefined) return d[name];
  if (d && d.default && d.default[name] !== undefined) return d.default[name];
  return undefined;
}

let Canvas, OrbitControls, useGLTF, Center, Html;
let useThree, Box3, Vector3, BufferAttribute, DoubleSide;
let hasThreeJsDeps = false;

try {
  const fiberModule = require('@react-three/fiber');
  const dreiModule = require('@react-three/drei');
  const threeModule = require('three');
  // Components: any non-undefined export (function, forwardRef, memo).
  Canvas = pickValue(fiberModule, 'Canvas');
  OrbitControls = pickValue(dreiModule, 'OrbitControls');
  Center = pickValue(dreiModule, 'Center');
  Html = pickValue(dreiModule, 'Html');
  // Hooks: genuinely functions.
  useThree = pickFn(fiberModule, 'useThree');
  useGLTF = pickFn(dreiModule, 'useGLTF');
  Box3 = (typeof ThreeBox3 === 'function') ? ThreeBox3 : pickFn(threeModule, 'Box3');
  Vector3 = (typeof ThreeVector3 === 'function') ? ThreeVector3 : pickFn(threeModule, 'Vector3');
  BufferAttribute = (typeof ThreeBufferAttribute === 'function')
    ? ThreeBufferAttribute
    : pickFn(threeModule, 'BufferAttribute');
  DoubleSide = (ThreeDoubleSide !== undefined) ? ThreeDoubleSide : pickValue(threeModule, 'DoubleSide');
  hasThreeJsDeps = !!(Canvas && useThree && OrbitControls && useGLTF && Center && Html);
} catch (error) {
  console.log('Three.js dependencies not available, using fallback mode');
  hasThreeJsDeps = false;
}

// Default framing: the model's XY bounds occupy this fraction of the
// tighter viewport axis (the lesser of width/height for a typical model).
const DEFAULT_FILL_FRACTION = 0.8;

function cameraDistanceToFill(objectWidth, objectHeight, camera, fill = DEFAULT_FILL_FRACTION) {
  const fillFraction = Math.min(Math.max(fill, 0.05), 1);
  const fovY = (camera.fov * Math.PI) / 180;
  const aspect = camera.aspect > 1e-6 ? camera.aspect : 1;
  const visibleHeightAt1 = 2 * Math.tan(fovY / 2);
  const visibleWidthAt1 = visibleHeightAt1 * aspect;
  const distForHeight = objectHeight / (visibleHeightAt1 * fillFraction);
  const distForWidth = objectWidth / (visibleWidthAt1 * fillFraction);
  return Math.max(distForHeight, distForWidth, 0.01);
}

// Lighting: ev-world's GLB preview rig (front-end/src/features/models/
// GlbPreview.tsx), the viewer the same generator's models look right in.
// No environment map and no tone mapping (<Canvas flat>) — deliberately, as
// there: the model's albedo is the look, and the directional lights sit on the
// camera's side. The rig this replaces (studio HDRI at 0.45, ambient 0.2, a
// 0.15 point light 15 units away, Neutral tone mapping at 0.9) lit the back
// and top of the model ~2.5× more than the face the camera sees, then the
// Neutral toe crushed the shadows — generated models read as gloomy and dull.
// It also fetched the HDR from raw.githack.com; a blocked CDN blanked the
// whole preview. Intensities are three r155+ physical units: 1.8 ambient ≈
// 0.57 × albedo on every face; the key adds up to 0.70 more.
const AMBIENT_INTENSITY = 1.8;
const KEY_INTENSITY = 2.2;
const KEY_POSITION = [1, 2, 1.5];
const FILL_INTENSITY = 0.8;
const FILL_POSITION = [-1, 0.5, -1];

// ev-world's rule for generated models (back-end/tools/glb-optim/
// prep-generated.mjs): with no environment map a metal has no diffuse term and
// renders near-black, so metalness is capped at 0 (glTF multiplies factor ×
// map channel, so the MR map's roughness keeps working).
const MAX_METALNESS = 0;

/** Smooth, area-weighted normals keyed by POSITION — the same algorithm as
 *  ev-world's prep-generated.mjs and tools/GlbOptimizer.js. Not three's
 *  computeVertexNormals(): that averages per INDEX, which leaves a hard crease
 *  at every UV seam (exactly where a generated texture atlas splits the mesh)
 *  and comes out flat on non-indexed geometry. */
function addSmoothNormals(geometry) {
  const position = geometry.attributes.position;
  if (!position) return false;
  const count = position.count;
  const canonical = new Uint32Array(count);
  const byPosition = new Map();
  for (let i = 0; i < count; i++) {
    // 5 decimals: welds float noise between duplicated seam vertices only.
    const key = position.getX(i).toFixed(5) + ',' + position.getY(i).toFixed(5) + ',' + position.getZ(i).toFixed(5);
    const existing = byPosition.get(key);
    if (existing === undefined) {
      byPosition.set(key, i);
      canonical[i] = i;
    } else {
      canonical[i] = existing;
    }
  }

  const index = geometry.index;
  const triangleCount = Math.floor((index ? index.count : count) / 3);
  const sums = new Float32Array(count * 3);
  for (let t = 0; t < triangleCount; t++) {
    const i0 = index ? index.getX(t * 3) : t * 3;
    const i1 = index ? index.getX(t * 3 + 1) : t * 3 + 1;
    const i2 = index ? index.getX(t * 3 + 2) : t * 3 + 2;
    const ax = position.getX(i0), ay = position.getY(i0), az = position.getZ(i0);
    const e1x = position.getX(i1) - ax, e1y = position.getY(i1) - ay, e1z = position.getZ(i1) - az;
    const e2x = position.getX(i2) - ax, e2y = position.getY(i2) - ay, e2z = position.getZ(i2) - az;
    // Unnormalised cross product == area weighting.
    const nx = e1y * e2z - e1z * e2y;
    const ny = e1z * e2x - e1x * e2z;
    const nz = e1x * e2y - e1y * e2x;
    for (const v of [i0, i1, i2]) {
      const k = canonical[v] * 3;
      sums[k] += nx;
      sums[k + 1] += ny;
      sums[k + 2] += nz;
    }
  }

  const normals = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const k = canonical[i] * 3;
    let nx = sums[k], ny = sums[k + 1], nz = sums[k + 2];
    const len = Math.hypot(nx, ny, nz);
    if (len > 0) {
      nx /= len; ny /= len; nz /= len;
    } else {
      nx = 0; ny = 1; nz = 0; // degenerate vertex: a stable direction, not NaN
    }
    normals[i * 3] = nx;
    normals[i * 3 + 1] = ny;
    normals[i * 3 + 2] = nz;
  }
  geometry.setAttribute('normal', new BufferAttribute(normals, 3));
  return true;
}

/** Runtime copy of ev-world's generated-model prep: metalness cap, doubleSided,
 *  smooth normals where the file has none. Self-host generations already get
 *  this at import (GlbOptimizer / the server optimizer); Tencent-era models
 *  and raw-fallback imports were stored untouched, and this is the only place
 *  they get it. useGLTF caches the scene per URL, so it runs once per scene;
 *  every step is idempotent anyway. Must never throw (CRA overlay). */
function prepGeneratedModel(scene) {
  if (!scene || (scene.userData && scene.userData.evPrepped)) return;
  try {
    scene.traverse((obj) => {
      if (!obj.isMesh || !obj.geometry) return;
      const normalsAdded = !obj.geometry.attributes.normal
        && typeof BufferAttribute === 'function'
        && addSmoothNormals(obj.geometry);
      const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
      materials.forEach((material) => {
        if (!material) return;
        let changed = false;
        if (normalsAdded && material.flatShading) {
          // GLTFLoader turned this on because NORMAL was missing.
          material.flatShading = false;
          changed = true;
        }
        if (typeof material.metalness === 'number' && material.metalness > MAX_METALNESS) {
          material.metalness = MAX_METALNESS;
          changed = true;
        }
        if (DoubleSide !== undefined && material.side !== DoubleSide) {
          material.side = DoubleSide;
          changed = true;
        }
        if (changed) material.needsUpdate = true;
      });
    });
  } catch (err) {
    console.warn('3D model prep failed:', err && err.message);
  }
  scene.userData.evPrepped = true;
}

class ThreeDViewerErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    console.error('3D viewer runtime error:', error);
    if (typeof this.props.onError === 'function') {
      this.props.onError(error);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <Box
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <Alert color="red" icon={<IconAlertCircle size={18} />} style={{ maxWidth: '320px' }}>
            <Text size="sm">3D model preview is temporarily unavailable.</Text>
            <Text size="xs" c="dimmed">
              {this.state.error.message || 'The generated model is not ready yet.'}
            </Text>
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// In-canvas UI: PLAIN DOM ONLY, never Mantine (or any context-consuming
// component).
//
// drei's <Html> does not portal into our React tree — it calls
// ReactDOM.createRoot() on a detached element and renders its children into
// that fresh root (see @react-three/drei/web/Html.js). A separate root inherits
// NO context, so a Mantine component in there throws "MantineProvider was not
// found in component tree" and the whole viewer unmounts. The dialog hit this
// every time (it shows the spinner because it has no loading image); the inline
// preview only when it fell into one of these branches.
// ---------------------------------------------------------------------------

const CanvasSpinner = ({ size = 40, color = '#AD1179' }) => (
  // SMIL rather than a CSS animation: no @keyframes rule has to reach the
  // element, so the spinner is self-contained inside the detached root.
  <svg
    width={size}
    height={size}
    viewBox="0 0 44 44"
    style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.35))' }}
  >
    <circle cx="22" cy="22" r="18" fill="none" stroke={color} strokeOpacity="0.2" strokeWidth="4" />
    <path d="M22 4a18 18 0 0 1 18 18" fill="none" stroke={color} strokeWidth="4" strokeLinecap="round">
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 22 22"
        to="360 22 22"
        dur="0.9s"
        repeatCount="indefinite"
      />
    </path>
  </svg>
);

const NOTICE_TONES = {
  red: { background: '#fdecee', border: '#f1aeb5', title: '#b02a37' },
  yellow: { background: '#fff8e1', border: '#ffe08a', title: '#8a6d3b' },
};

const CanvasNotice = ({ tone = 'red', title, detail }) => {
  const palette = NOTICE_TONES[tone] || NOTICE_TONES.red;
  return (
    <div
      style={{
        minWidth: 200,
        maxWidth: 320,
        padding: '10px 14px',
        borderRadius: 8,
        background: palette.background,
        border: '1px solid ' + palette.border,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, color: palette.title }}>{title}</div>
      {detail && (
        <div style={{ fontSize: 12, marginTop: 4, color: '#6c757d', wordBreak: 'break-word' }}>
          {detail}
        </div>
      )}
    </div>
  );
};

// Proxy URL Resolver Component
let ProxyURLResolver;

if (hasThreeJsDeps) {
  ProxyURLResolver = function({ url, onUrlReady, onError }) {
    // Callbacks live in refs so the effect depends on the URL ALONE. With the
    // callbacks in the dependency list, every parent render (inline-arrow
    // props change identity each time) re-ran the resolution — in Safari,
    // where every attempt fails, that produced an endless request stream.
    const onUrlReadyRef = React.useRef(onUrlReady);
    const onErrorRef = React.useRef(onError);
    onUrlReadyRef.current = onUrlReady;
    onErrorRef.current = onError;

    useEffect(() => {
      let cancelled = false;

      async function resolveUrl() {
        try {
          const processedUrl = await getGLBUrl(url);
          if (cancelled) {
            return;
          }
          onUrlReadyRef.current(processedUrl);
        } catch (err) {
          if (cancelled) {
            return;
          }
          console.warn('GLB resolution failed:', url, err && err.message);
          onErrorRef.current(err.message);
        }
      }

      if (url) {
        resolveUrl();
      }

      return () => {
        cancelled = true;
      };
    }, [url]);

    return null; // This component doesn't render anything
  };
}

// Simple Model Component that uses resolved proxy URL
let Model;

if (hasThreeJsDeps) {
  Model = function({ url, scale = 1, onLoaded }) {
    const groupRef = useRef(null);
    const framedForUrlRef = useRef(null);
    const { camera, controls, size: viewSize, invalidate } = useThree();

    // Parent only mounts Model with a resolved URL; keep hooks unconditional.
    const gltfResult = useGLTF(url);

    // Before the scene is first drawn; a no-op on useGLTF's cached re-renders.
    React.useMemo(() => prepGeneratedModel(gltfResult && gltfResult.scene), [gltfResult]);

    // Notify parent when the actual GLTF finishes loading
    useEffect(() => {
      if (gltfResult && gltfResult.scene && typeof onLoaded === 'function') {
        onLoaded();
      }
    }, [gltfResult, onLoaded]);

    // Frame so the model occupies ~80% of the lesser viewport dimension.
    // Must never throw: CRA's overlay would cover the whole editor.
    useLayoutEffect(() => {
      const group = groupRef.current;
      if (!url || !group || !gltfResult || !gltfResult.scene) {
        return undefined;
      }
      if (framedForUrlRef.current === url) {
        return undefined;
      }

      const applyFrame = () => {
        try {
          if (typeof Box3 !== 'function' || typeof Vector3 !== 'function') {
            framedForUrlRef.current = url;
            return true;
          }
          if (!viewSize || viewSize.height <= 0) {
            return false;
          }
          camera.aspect = viewSize.width / viewSize.height;
          group.updateWorldMatrix(true, true);
          const box = new Box3().setFromObject(group);
          if (box.isEmpty()) {
            return false;
          }
          const size = box.getSize(new Vector3());
          const objectWidth = Math.max(size.x, 1e-6);
          const objectHeight = Math.max(size.y, 1e-6);
          const distance = cameraDistanceToFill(objectWidth, objectHeight, camera);
          // Keep the camera outside the model's depth so the near plane
          // does not clip backpacks / protruding geometry.
          const z = Math.max(distance, size.z * 0.55 + 0.01);
          camera.position.set(0, 0, z);
          camera.near = Math.max(z / 100, 0.01);
          camera.far = Math.max(z * 100, 100);
          camera.lookAt(0, 0, 0);
          camera.updateProjectionMatrix();
          if (controls) {
            controls.target.set(0, 0, 0);
            controls.update();
          }
          invalidate();
          framedForUrlRef.current = url;
          return true;
        } catch (err) {
          console.warn('3D auto-frame failed:', err && err.message);
          framedForUrlRef.current = url;
          return true;
        }
      };

      if (applyFrame()) {
        return undefined;
      }
      const frameId = requestAnimationFrame(applyFrame);
      return () => cancelAnimationFrame(frameId);
    }, [gltfResult, url, camera, controls, viewSize, invalidate]);

    if (!gltfResult || !gltfResult.scene) {
      return (
        <Html center>
          <CanvasNotice tone="red" title="Failed to load 3D model" />
        </Html>
      );
    }

    console.log('✅ Model loaded successfully from:', url);

    return (
      <Center>
        <group ref={groupRef}>
          <primitive object={gltfResult.scene} scale={[scale, scale, scale]} />
        </group>
      </Center>
    );
  };
}

// Main Model Wrapper with Proxy Support
let ModelWithProxy;

if (hasThreeJsDeps) {
  ModelWithProxy = function({ originalUrl, scale = 1, onLoadStart, onLoadComplete, showLoadingUI = true }) {
    const [proxyUrl, setProxyUrl] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    // Parent callbacks via refs: their identities change on every parent
    // render (inline arrows) and must never re-trigger effects or resolution.
    const onLoadStartRef = React.useRef(onLoadStart);
    const onLoadCompleteRef = React.useRef(onLoadComplete);
    onLoadStartRef.current = onLoadStart;
    onLoadCompleteRef.current = onLoadComplete;

    useEffect(() => {
      if (loading && onLoadStartRef.current) {
        onLoadStartRef.current();
      }
    }, [loading]);

    const handleUrlReady = React.useCallback((url) => {
      setProxyUrl(url);
      setLoading(false);
      setError(null);
      // Do not call onLoadComplete here; wait until GLTF fully loads in Model
    }, []);

    const handleError = React.useCallback((errorMsg) => {
      setError(errorMsg);
      setLoading(false);
      setProxyUrl(null);
      if (onLoadCompleteRef.current) {
        onLoadCompleteRef.current();
      }
    }, []);

    const handleRetry = React.useCallback(() => {
      // User-initiated: drop the failure memo so the resolver actually
      // re-attempts instead of rejecting from the negative cache.
      clearGLBFailure(originalUrl);
      setError(null);
      setProxyUrl(null);
      setLoading(true);
    }, [originalUrl]);

    // Stable identity for Model's onLoaded effect — the raw parent prop
    // changes identity every render and would re-fire the loaded callback.
    const handleModelLoaded = React.useCallback(() => {
      if (onLoadCompleteRef.current) {
        onLoadCompleteRef.current();
      }
    }, []);

    if (loading) {
      return (
        <>
          <ProxyURLResolver 
            url={originalUrl} 
            onUrlReady={handleUrlReady} 
            onError={handleError} 
          />
          {showLoadingUI && (
            <Html center>
              <CanvasSpinner size={40} />
            </Html>
          )}
        </>
      );
    }

    if (error) {
      // Plain DOM only in here (see the CanvasNotice comment). Manual retry —
      // never automatic: an auto-retry on a CORS-blocked host is exactly the
      // endless request loop this component used to produce in Safari.
      return (
        <Html center>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <CanvasNotice tone="red" title="Failed to load 3D model" detail={error} />
            <button
              type="button"
              onClick={handleRetry}
              style={{
                padding: '4px 14px',
                borderRadius: 14,
                border: '1px solid #ad1179',
                background: '#fff',
                color: '#ad1179',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        </Html>
      );
    }

    // Only render Model when we have a valid proxy URL
    if (!proxyUrl) {
      return (
        <Html center>
          <CanvasNotice tone="yellow" title="Waiting for proxy URL resolution..." />
        </Html>
      );
    }

    return <Model url={proxyUrl} scale={scale} onLoaded={handleModelLoaded} />;
  };
}

// Simple Loading Component
const Loader = function({ showLoadingUI = true }) {
  if (!showLoadingUI) {
    // When we have a background image, don't show the default loading UI
    return null;
  }
  
  return (
    <Html center>
      <CanvasSpinner size={36} color="#ffffff" />
    </Html>
  );
};

// Fallback component when Three.js is not available
function ThreeDFallback({ modelUrl }) {
  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: 24,
        backgroundColor: '#f5f5f5',
        border: '2px dashed #ccc',
        borderRadius: 8,
        textAlign: 'center',
      }}
    >
      <Text fw={600} size="lg" c={BRAND_PRIMARY}>
        3D Model Viewer
      </Text>
      <Text size="sm" c="dimmed">
        Three.js dependencies need to be installed for full 3D viewing.
      </Text>
    </Box>
  );
}

// Main Simple 3D Model Viewer Component
const ThreeDModelViewer_Simple = ({ 
  modelUrl, 
  width = '100%', 
  height = '400px',
  scale = 1.0,
  cameraDistance = 5,
  fov = 50,
  onLoadStart,
  onLoadComplete,
  loadingImageUrl,
  allowFullscreen = false,
}) => {
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const viewerRef = useRef(null);
  
  useEffect(() => {
    setIsModelLoaded(false);
  }, [modelUrl]);

  useEffect(() => {
    if (!allowFullscreen) return undefined;

    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(fullscreenElement === viewerRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [allowFullscreen]);
  
  const handleLoadStart = React.useCallback(() => {
    setIsModelLoaded(false);
    if (onLoadStart) {
      onLoadStart();
    }
  }, [onLoadStart]);
  
  const handleLoadComplete = React.useCallback(() => {
    setIsModelLoaded(true);
    if (onLoadComplete) {
      onLoadComplete();
    }
  }, [onLoadComplete]);

  const handleRuntimeError = React.useCallback(() => {
    setIsModelLoaded(false);
    if (onLoadComplete) {
      onLoadComplete();
    }
  }, [onLoadComplete]);

  const fullscreenSupported = typeof document !== 'undefined' && (
    document.fullscreenEnabled ||
    document.webkitFullscreenEnabled ||
    (viewerRef.current && typeof viewerRef.current.webkitRequestFullscreen === 'function')
  );

  const toggleFullscreen = useCallback(async () => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    try {
      if (isFullscreen) {
        const exitFullscreen = document.exitFullscreen || document.webkitExitFullscreen;
        if (exitFullscreen) await exitFullscreen.call(document);
      } else {
        const requestFullscreen = viewer.requestFullscreen || viewer.webkitRequestFullscreen;
        if (requestFullscreen) await requestFullscreen.call(viewer);
      }
    } catch (error) {
      console.warn('Unable to change 3D viewer fullscreen state:', error);
    }
  }, [isFullscreen]);

  if (!modelUrl) {
    return (
      <Box
        style={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          border: '1px dashed #ccc',
          borderRadius: 4,
        }}
      >
        <Text size="sm" c="dimmed">
          No 3D model to display
        </Text>
      </Box>
    );
  }

  // If Three.js dependencies are not available, show fallback
  if (!hasThreeJsDeps) {
    return (
      <Box style={{ width, height }}>
        <ThreeDFallback modelUrl={modelUrl} />
      </Box>
    );
  }

  const fullscreenLabel = isFullscreen
    ? (Localization.strings.EXIT_3D_FULLSCREEN || 'Exit fullscreen')
    : (Localization.strings.VIEW_3D_FULLSCREEN || 'View fullscreen');
  
  // Full Three.js implementation
  return (
    <Box
      ref={viewerRef}
      style={{
        width: isFullscreen ? '100vw' : width,
        height: isFullscreen ? '100vh' : height,
        border: '1px solid #e0e0e0',
        borderRadius: isFullscreen ? 0 : 4,
        position: 'relative',
        overflow: 'hidden',
        backgroundImage: (loadingImageUrl && !isModelLoaded) ? `url(${loadingImageUrl})` : 'none',
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        // The page body, as in ev-world's preview: mid-tone models read flat
        // against the light grey this used to be.
        backgroundColor: 'var(--mantine-color-body)',
        transition: 'background-image 0.3s ease-out'
      }}
    >
      {!isModelLoaded && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          <MantineLoader
            size={48}
            style={{
              color: '#AD1179',
              filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.8))'
            }}
          />
        </Box>
      )}
      {allowFullscreen && fullscreenSupported && (
        <Tooltip label={fullscreenLabel} withArrow withinPortal={false}>
          <ActionIcon
            type="button"
            aria-label={fullscreenLabel}
            title={fullscreenLabel}
            variant="filled"
            color="dark"
            size="lg"
            onClick={toggleFullscreen}
            style={{
              position: 'absolute',
              top: 12,
              left: 12,
              zIndex: 3,
              backgroundColor: 'rgba(0, 0, 0, 0.65)',
            }}
          >
            {isFullscreen ? <IconArrowsMinimize size={20} /> : <IconArrowsMaximize size={20} />}
          </ActionIcon>
        </Tooltip>
      )}
      <ThreeDViewerErrorBoundary resetKey={modelUrl} onError={handleRuntimeError}>
        <Canvas
          flat
          camera={{ position: [0, 0, cameraDistance], fov }}
          style={{ 
            width: '100%', 
            height: '100%',
            display: 'block',
            position: 'absolute',
            top: 0,
            left: 0,
            // Keep canvas visible to retain GL context when dialogs open/close
            zIndex: isModelLoaded ? 1 : 0,
            opacity: isModelLoaded ? 1 : 0,
            visibility: 'visible',
            transition: 'opacity 0.5s ease-in'
          }}
          gl={{
            antialias: true,
            alpha: true,
            preserveDrawingBuffer: true,
            clearColor: 0x000000,
            clearAlpha: 0,
          }}
        >
          <ambientLight intensity={AMBIENT_INTENSITY} />
          <directionalLight position={KEY_POSITION} intensity={KEY_INTENSITY} />
          <directionalLight position={FILL_POSITION} intensity={FILL_INTENSITY} />

          <Suspense fallback={<Loader showLoadingUI={false} />}>
            <ModelWithProxy 
              originalUrl={modelUrl} 
              scale={scale} 
              onLoadStart={handleLoadStart}
              onLoadComplete={handleLoadComplete}
              showLoadingUI={false}
            />
          </Suspense>
          
          <OrbitControls 
            makeDefault
            enableZoom={true}
            enablePan={true}
            enableRotate={true}
            minDistance={0.01}
            maxDistance={1000}
          />
        </Canvas>
      </ThreeDViewerErrorBoundary>
    </Box>
  );
};

export default ThreeDModelViewer_Simple;
